import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe2, ChevronDown, Check } from "lucide-react";
import { LOCALES } from "@/locales/landing";

/**
 * LanguageSwitcher — small button + dropdown for switching between the
 * localized landing pages (`/`, `/es`, `/hi`, `/zh`, `/tl`, etc.).
 *
 * The URL scheme is: /  = English, /:code  = localized variant. When the user
 * picks a locale we route to the corresponding path — we do NOT try to swap
 * strings in-place, so browsers, SEO, and back/forward navigation all work
 * as expected (crucial for the /es, /hi, etc. hreflang alternates).
 */
export default function LanguageSwitcher({ current = "en", variant = "chip" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentLocale = LOCALES.find((l) => l.code === current) || LOCALES[0];
  const chipStyle = {
    border: "1px solid var(--border-stone)",
    background: "#fff",
    color: "var(--text-primary)",
  };
  const pathFor = (code) => (code === "en" ? "/" : `/${code}`);

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-ghost flex items-center gap-1.5 text-xs px-3"
        style={{
          ...chipStyle,
          padding: variant === "chip" ? "0.4rem 0.75rem" : "0.55rem 0.9rem",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        data-testid="language-switcher-btn"
      >
        <Globe2 className="w-3.5 h-3.5" style={{ color: "var(--action)" }} />
        <span className="font-medium">{currentLocale.flag} {currentLocale.nativeName}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 card-surface shadow-2xl anim-in z-50"
          style={{
            minWidth: 220,
            padding: 6,
            maxHeight: 360,
            overflowY: "auto",
          }}
          role="listbox"
          data-testid="language-switcher-menu"
        >
          {LOCALES.map((l) => {
            const active = l.code === current;
            return (
              <Link
                key={l.code}
                to={pathFor(l.code)}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-black/5 text-sm"
                style={{
                  color: "var(--text-primary)",
                  background: active ? "var(--ai-bg)" : "transparent",
                }}
                role="option"
                aria-selected={active}
                data-testid={`language-option-${l.code}`}
                hrefLang={l.code}
              >
                <span className="w-5 text-center">{l.flag}</span>
                <span className="flex-1 min-w-0 truncate">{l.nativeName}</span>
                <span className="text-[10px] uppercase tracking-wider opacity-60">{l.code}</span>
                {active && <Check className="w-3.5 h-3.5" style={{ color: "var(--action)" }} />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
